import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import type { ButtonProps } from '@gitbook/spine-react';
import { confirm, DialogRoot, toast, useDialogTriggerState } from '@gitbook/spine-react';

import { apiClient } from '../../state';
import { EditTranslationDialog } from './EditTranslationDialog';

/**
 * Button to control a translation profile.
 */
export function TranslationControlButton(
    props: {
        organizationId: string;
        translation: api.Translation;
    } & Omit<ButtonProps, 'label' | 'onPress'>
) {
    const { organizationId, translation, ...buttonProps } = props;

    const editDialog = useDialogTriggerState();

    return (
        <>
            <PaletteButton
                label="More actions"
                iconOnly
                kind="blank"
                leadingIcon={Icon.MoreHorizontal}
                {...buttonProps}
                palette={() => {
                    return createPalette({
                        groups: [
                            {
                                key: 'admin',
                                commands: [
                                    {
                                        key: 'edit',
                                        title: 'Edit',
                                        icon: Icon.Pencil,
                                        trigger: () => {
                                            editDialog.open();
                                        },
                                    },
                                    {
                                        key: 'rerun',
                                        title: 'Re-run',
                                        icon: Icon.Refresh,
                                        trigger: () => {
                                            void confirm({
                                                title: 'Re-run translation',
                                                description: (
                                                    <>
                                                        This will re-run the entire translation and
                                                        may consume additional words.
                                                    </>
                                                ),
                                                confirmLabel: 'Re-run',
                                                onConfirm: async (_event, { preventClose }) => {
                                                    await toast.catch(
                                                        async () => {
                                                            await apiClient.translations.runTranslation(
                                                                organizationId,
                                                                translation.id
                                                            );

                                                            analytics.logEvent({
                                                                type: 'translation_rerun',
                                                                organization: organizationId,
                                                                sourceLanguage:
                                                                    translation.source.language,
                                                                targetLanguage:
                                                                    translation.language,
                                                            });

                                                            toast.success('Translation started');
                                                        },
                                                        'Error while running the translation',
                                                        {
                                                            onReject: preventClose,
                                                        }
                                                    );
                                                },
                                            });
                                        },
                                    },
                                    {
                                        key: 'delete',
                                        title: 'Delete',
                                        icon: Icon.Trashcan,
                                        trigger: () => {
                                            void confirm({
                                                kind: 'danger',
                                                title: 'Delete translation',
                                                description: (
                                                    <>
                                                        {' '}
                                                        This action will prevent all translations
                                                        configured with this workflow to work.
                                                    </>
                                                ),

                                                confirmLabel: 'Delete',
                                                onConfirm: async (_event, { preventClose }) => {
                                                    await toast.catch(
                                                        async () => {
                                                            await apiClient.translations.deleteTranslation(
                                                                organizationId,
                                                                translation.id
                                                            );

                                                            analytics.logEvent({
                                                                type: 'translation_delete',
                                                                organization: organizationId,
                                                                sourceLanguage:
                                                                    translation.source.language,
                                                                targetLanguage:
                                                                    translation.language,
                                                            });

                                                            toast.success('Translation deleted');
                                                        },
                                                        'Error while deleting the translation',
                                                        {
                                                            onReject: preventClose,
                                                        }
                                                    );
                                                },
                                            });
                                        },
                                    },
                                ],
                            },
                        ],
                    });
                }}
            />
            <DialogRoot {...editDialog.rootProps}>
                <EditTranslationDialog organizationId={organizationId} translation={translation} />
            </DialogRoot>
        </>
    );
}
